import * as React from "react";
import TemplateColumnsSection from "./TemplateColumnsSection";
import TemplateEntitySection from "./TemplateEntitySection";
import TemplateFiltersSection from "./TemplateFiltersSection";
import { TemplateFeedbackBanners, TemplateTopStatusBanners } from "./TemplateStatusBanners";
import TemplateUdfSection from "./TemplateUdfSection";
import { TemplateGenerateSection, TemplateSaveSection, TemplateSettingsSection } from "./TemplateWorkflowSections";
import { useTemplateTabViewStyles } from "./TemplateTabViewStyles";
import type { TemplateTabViewProps } from "./TemplateTabViewTypes";

const TemplateTabView: React.FC<TemplateTabViewProps> = ({
  isLoadingConfiguration,
  workbookDetected,
  operationError,
  operationNotice,
  isWorkbookBannerDismissed,
  setIsWorkbookBannerDismissed,
  selectedEntityType,
  entityTypeQuery,
  setEntityTypeQuery,
  entityNames,
  loadingEntityNames,
  loadingFilters,
  loadingColumns,
  loadingRoles,
  selectedFilters,
  availableFilters,
  showFilterPicker,
  setShowFilterPicker,
  selectedColumns,
  availableColumns,
  showColumnPicker,
  setShowColumnPicker,
  udfCategories,
  selectedUdfs,
  includeUdfs,
  setIncludeUdfs,
  templateName,
  setTemplateName,
  activeEmptyVisibleSheetName,
  dataReferenceMode,
  setDataReferenceMode,
  roles,
  selectedRoles,
  setSelectedRoles,
  isGenerating,
  isSaving,
  hasGeneratedWorkbook,
  handleClearAll,
  handleEntityTypeChange,
  handleAddFilter,
  handleResetFilters,
  handleRemoveFilter,
  handleFilterDrop,
  handleFilterTypeChange,
  handleFilterDisplayNameChange,
  handleFilterRequiredChange,
  handleFilterCascadeChange,
  handleAddColumn,
  handleResetColumns,
  handleRemoveColumn,
  handleColumnDisplayNameChange,
  handleColumnExcelFormatChange,
  handleColumnDrop,
  handleUdfCategoryToggle,
  handleUdfItemToggle,
  handleMasterUdfToggle,
  handleGenerateWorkbook,
  handleSaveTemplate,
  setHasGeneratedWorkbook,
}) => {
  const styles = useTemplateTabViewStyles();
  const isConfigurationLocked = isLoadingConfiguration;
  const isFormValid = Boolean(selectedEntityType && selectedFilters.length > 0 && selectedColumns.length > 0 && templateName.trim().length > 0);

  return (
    <div className={styles.root}>
      <div className={styles.pageTitle}>TEMPLATE BUILDER</div>
      <div className={styles.pageSubtitle}>Create or edit a user-defined template.</div>

      <TemplateTopStatusBanners
        isLoadingConfiguration={isLoadingConfiguration}
        workbookDetected={workbookDetected}
        operationError={operationError}
        isWorkbookBannerDismissed={isWorkbookBannerDismissed}
        setIsWorkbookBannerDismissed={setIsWorkbookBannerDismissed}
        isGenerating={isGenerating}
        isSaving={isSaving}
        handleClearAll={handleClearAll}
      />

      <TemplateEntitySection
        styles={styles}
        selectedEntityType={selectedEntityType}
        entityTypeQuery={entityTypeQuery}
        setEntityTypeQuery={setEntityTypeQuery}
        entityNames={entityNames}
        loadingEntityNames={loadingEntityNames}
        handleEntityTypeChange={handleEntityTypeChange}
      />

      {selectedEntityType && (
        <TemplateFiltersSection
          styles={styles}
          isConfigurationLocked={isConfigurationLocked}
          loadingFilters={loadingFilters}
          entityNames={entityNames}
          selectedFilters={selectedFilters}
          availableFilters={availableFilters}
          showFilterPicker={showFilterPicker}
          setShowFilterPicker={setShowFilterPicker}
          setShowColumnPicker={setShowColumnPicker}
          handleAddFilter={handleAddFilter}
          handleResetFilters={handleResetFilters}
          handleRemoveFilter={handleRemoveFilter}
          handleFilterDrop={handleFilterDrop}
          handleFilterTypeChange={handleFilterTypeChange}
          handleFilterDisplayNameChange={handleFilterDisplayNameChange}
          handleFilterRequiredChange={handleFilterRequiredChange}
          handleFilterCascadeChange={handleFilterCascadeChange}
        />
      )}

      {selectedEntityType && (
        <TemplateColumnsSection
          styles={styles}
          isConfigurationLocked={isConfigurationLocked}
          loadingColumns={loadingColumns}
          selectedColumns={selectedColumns}
          availableColumns={availableColumns}
          showColumnPicker={showColumnPicker}
          setShowColumnPicker={setShowColumnPicker}
          setShowFilterPicker={setShowFilterPicker}
          handleAddColumn={handleAddColumn}
          handleResetColumns={handleResetColumns}
          handleRemoveColumn={handleRemoveColumn}
          handleColumnDisplayNameChange={handleColumnDisplayNameChange}
          handleColumnExcelFormatChange={handleColumnExcelFormatChange}
          handleColumnDrop={handleColumnDrop}
        />
      )}

      {selectedEntityType && udfCategories.length > 0 && (
        <TemplateUdfSection
          styles={styles}
          isConfigurationLocked={isConfigurationLocked}
          udfCategories={udfCategories}
          selectedUdfs={selectedUdfs}
          includeUdfs={includeUdfs}
          setIncludeUdfs={setIncludeUdfs}
          handleUdfCategoryToggle={handleUdfCategoryToggle}
          handleUdfItemToggle={handleUdfItemToggle}
          handleMasterUdfToggle={handleMasterUdfToggle}
          setHasGeneratedWorkbook={setHasGeneratedWorkbook}
        />
      )}

      {selectedEntityType && (
        <TemplateSettingsSection
          styles={styles}
          templateName={templateName}
          setTemplateName={setTemplateName}
          setHasGeneratedWorkbook={setHasGeneratedWorkbook}
        />
      )}

      {selectedEntityType && (
        <TemplateGenerateSection
          styles={styles}
          isConfigurationLocked={isConfigurationLocked}
          activeEmptyVisibleSheetName={activeEmptyVisibleSheetName}
          dataReferenceMode={dataReferenceMode}
          setDataReferenceMode={setDataReferenceMode}
          setHasGeneratedWorkbook={setHasGeneratedWorkbook}
          isFormValid={isFormValid}
          isGenerating={isGenerating}
          isSaving={isSaving}
          handleGenerateWorkbook={handleGenerateWorkbook}
          handleClearAll={handleClearAll}
        />
      )}

      {selectedEntityType && (
        <TemplateSaveSection
          styles={styles}
          isConfigurationLocked={isConfigurationLocked}
          roles={roles}
          selectedRoles={selectedRoles}
          setSelectedRoles={setSelectedRoles}
          loadingRoles={loadingRoles}
          hasGeneratedWorkbook={hasGeneratedWorkbook}
          templateName={templateName}
          isGenerating={isGenerating}
          isSaving={isSaving}
          handleSaveTemplate={handleSaveTemplate}
        />
      )}

      <TemplateFeedbackBanners
        styles={styles}
        operationError={operationError}
        operationNotice={operationNotice}
        hasGeneratedWorkbook={hasGeneratedWorkbook}
      />
    </div>
  );
};

export default TemplateTabView;
